/**
 * Pop Quiz user settings adapter.
 *
 * Settings are stored as a single JSON blob under 'pp-pop-quiz_settings' in the
 * user_setting table, written via Django REST and read synchronously from
 * udData.me.settings via UserSettingsStore.
 *
 * The three logical settings (lastShownDate, lastQuizId, snoozedAt) are preserved
 * as named constants used as identifiers at call sites — they are no longer
 * individual storage keys.
 */

import {useCallback} from 'react';

import {UserSettingsStore} from '@udemy/common-stores';
import {useStores} from '@udemy/store-provider';

export const POP_QUIZ_SETTINGS = Object.freeze({
    /** ISO date when the pop quiz was last shown. Used for the weekly frequency cap. */
    lastShownDate: Object.freeze({
        settingCode: 'pp-pop-quiz_last-shown-date',
        defaultValue: '',
    }),
    /** ID of the last quiz shown. Used for quiz rotation (avoid repeating). */
    lastQuizId: Object.freeze({
        settingCode: 'pp-pop-quiz_last-quiz-id',
        defaultValue: '',
    }),
    /**
     * ISO date when the user requested the 60-day snooze.
     * Expiry is computed on read: snoozedAt + LONG_SNOOZE_DAYS > today.
     * Storing the request date (not the expiry) keeps the value portable —
     * if the snooze duration changes, old records still reflect user intent.
     */
    snoozedAt: Object.freeze({
        settingCode: 'pp-pop-quiz_snoozed-at',
        defaultValue: '',
    }),
});

export type PopQuizSetting = (typeof POP_QUIZ_SETTINGS)[keyof typeof POP_QUIZ_SETTINGS];

/** Shape of the JSON blob stored under `pp-pop-quiz_settings`. */
export interface PopQuizSettingsPayload {
    lastShownDate: string;
    lastQuizId: string;
    snoozedAt: string;
}

const EMPTY_PAYLOAD: PopQuizSettingsPayload = {lastShownDate: '', lastQuizId: '', snoozedAt: ''};

function parsePayload(raw: string | undefined): PopQuizSettingsPayload {
    if (!raw) {
        return {...EMPTY_PAYLOAD};
    }
    try {
        return JSON.parse(raw) as PopQuizSettingsPayload;
    } catch {
        return {...EMPTY_PAYLOAD};
    }
}

function payloadKey(setting: PopQuizSetting): keyof PopQuizSettingsPayload {
    switch (setting.settingCode) {
        case POP_QUIZ_SETTINGS.lastShownDate.settingCode:
            return 'lastShownDate';
        case POP_QUIZ_SETTINGS.lastQuizId.settingCode:
            return 'lastQuizId';
        default:
            return 'snoozedAt';
    }
}

/**
 * Hook providing get/set access to pop quiz settings via UserSettingsStore.
 * - `getPopQuizSetting` reads synchronously from udData.me.settings (no network call).
 * - `setPopQuizSetting` writes asynchronously via POST /users/me/settings/.
 *   Call fire-and-forget (`void setPopQuizSetting(...)`) in event handlers.
 */
export function usePopQuizSettings() {
    const [userSettingsStore] = useStores([UserSettingsStore]);

    const getPopQuizSetting = useCallback(
        (setting: PopQuizSetting): string => {
            const raw = userSettingsStore.get(UserSettingsStore.SETTINGS.popQuizSettings) as
                | string
                | undefined;
            const payload = parsePayload(raw);
            return payload[payloadKey(setting)] ?? setting.defaultValue;
        },
        [userSettingsStore],
    );

    const setPopQuizSetting = useCallback(
        async (setting: PopQuizSetting, value: string): Promise<void> => {
            const raw = userSettingsStore.get(UserSettingsStore.SETTINGS.popQuizSettings) as
                | string
                | undefined;
            const payload = parsePayload(raw);
            payload[payloadKey(setting)] = value;
            await userSettingsStore.set(
                UserSettingsStore.SETTINGS.popQuizSettings,
                JSON.stringify(payload),
            );
        },
        [userSettingsStore],
    );

    return {getPopQuizSetting, setPopQuizSetting};
}
